import { useState } from "react";
import { Link } from "react-router-dom";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button, DarkSelect, Input, PageHeader, Pill, SortableTh, Table, Td, Th } from "@/components/ui";
import { confirmPopup } from "@/components/popup";
import { TrashIcon } from "@/components/icons";
import { PaginationBottom, PaginationTop } from "@/components/Pagination";
import { ArchiveBanner } from "@/components/ArchiveBanner";

interface User {
  id: number;
  telegram_id: number;
  username: string | null;
}

interface ServerRef {
  id: number;
  name: string;
  host: string;
}

interface Access {
  id: number;
  user_id: number;
  server_id: number;
  secret: string;
  access_type: string;
  is_active: boolean;
  activated_at: string;
  expires_at: string;
  user: User | null;
  server: ServerRef | null;
}

interface AccessesResponse {
  accesses: Access[];
  total: number;
}

interface ServerItem {
  id: number;
  name: string;
  server_type: string;
}

interface ServersResponse {
  servers: ServerItem[];
}

type SortCol = "id" | "activated_at" | "expires_at" | "user_id" | "server_id";
type SortDir = "asc" | "desc";
type TypeFilter = "" | "test" | "paid";
type StatusFilter = "" | "active" | "inactive";

const TYPE_OPTS: { key: TypeFilter; label: string }[] = [
  { key: "", label: "Все" },
  { key: "paid", label: "Платные" },
  { key: "test", label: "Тест" },
];
const STATUS_OPTS: { key: StatusFilter; label: string }[] = [
  { key: "", label: "Все" },
  { key: "active", label: "Активные" },
  { key: "inactive", label: "Неактивные" },
];

const fmt = (iso: string) =>
  new Date(iso).toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });

export default function Accesses() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("");
  const [serverFilter, setServerFilter] = useState<string>("");
  const [sort, setSort] = useState<SortCol>("activated_at");
  const [dir, setDir] = useState<SortDir>("desc");
  const qc = useQueryClient();

  const offset = (page - 1) * pageSize;

  const toggleSort = (col: string) => {
    const key = col as SortCol;
    if (sort !== key) {
      setSort(key);
      setDir("desc");
    } else if (dir === "desc") {
      setDir("asc");
    } else {
      setDir("desc");
    }
    setPage(1);
  };

  const listParams = new URLSearchParams({
    limit: String(pageSize),
    offset: String(offset),
    sort,
    direction: dir,
  });
  if (search.trim()) listParams.set("search", search.trim());
  if (typeFilter) listParams.set("access_type", typeFilter);
  if (statusFilter) listParams.set("status", statusFilter);
  if (serverFilter) listParams.set("server_id", serverFilter);

  const q = useQuery<AccessesResponse>({
    queryKey: [
      "accesses",
      page,
      pageSize,
      search.trim(),
      typeFilter,
      statusFilter,
      serverFilter,
      sort,
      dir,
    ],
    queryFn: () => api(`/api/admin/accesses?${listParams.toString()}`),
    placeholderData: keepPreviousData,
  });

  const servers = useQuery<ServersResponse>({
    queryKey: ["servers"],
    queryFn: () => api("/api/admin/servers"),
  });

  const deactivate = useMutation({
    mutationFn: (id: number) =>
      api(`/api/admin/accesses/${id}/deactivate`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["accesses"] }),
  });

  const extend = useMutation({
    mutationFn: ({ id, days }: { id: number; days: number }) =>
      api(`/api/admin/accesses/${id}/extend`, {
        method: "POST",
        body: JSON.stringify({ days }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["accesses"] }),
  });

  const remove = useMutation({
    mutationFn: (id: number) =>
      api(`/api/admin/accesses/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["accesses"] }),
  });

  const accesses = q.data?.accesses ?? [];
  const total = q.data?.total ?? 0;
  const fetching = q.isFetching;

  return (
    <div className="p-4 md:p-8">
      <PageHeader
        title="Доступы"
        right={<div className="text-zinc-400 text-sm">найдено: {total}</div>}
      />
      <ArchiveBanner what="Доступы старой модели (Server/Access)" />

      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <Input
          placeholder="Поиск: user_id, telegram_id или @username"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="w-full sm:w-96"
        />
        <div className="ml-auto">
          {total > 0 && (
            <PaginationTop
              total={total}
              pageSize={pageSize}
              onPageSizeChange={(n) => {
                setPageSize(n);
                setPage(1);
              }}
            />
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <FilterGroup
          options={TYPE_OPTS}
          value={typeFilter}
          onChange={(v) => {
            setTypeFilter(v);
            setPage(1);
          }}
        />
        <FilterGroup
          options={STATUS_OPTS}
          value={statusFilter}
          onChange={(v) => {
            setStatusFilter(v);
            setPage(1);
          }}
        />
        <div className="w-56">
          <DarkSelect
            value={serverFilter}
            onChange={(v) => {
              setServerFilter(v);
              setPage(1);
            }}
            placeholder="Любой сервер"
            options={[
              { value: "", label: "Любой сервер" },
              ...(servers.data?.servers ?? []).map((s) => ({
                value: String(s.id),
                label: `#${s.id} ${s.name}`,
                hint: s.server_type,
              })),
            ]}
          />
        </div>
      </div>

      {q.isLoading && !q.data && <div className="text-zinc-400">Загрузка…</div>}

      {accesses.length === 0 && !q.isLoading && (
        <div className="text-zinc-500">Ничего не найдено.</div>
      )}

      {accesses.length > 0 && (
        <>
          <div className={fetching ? "opacity-60 transition-opacity" : "transition-opacity"}>
            <Table>
              <thead>
                <tr>
                  <SortableTh
                    sortKey="user_id"
                    currentSort={sort}
                    currentDir={dir}
                    onSort={toggleSort}
                  >
                    Пользователь
                  </SortableTh>
                  <SortableTh
                    sortKey="server_id"
                    currentSort={sort}
                    currentDir={dir}
                    onSort={toggleSort}
                  >
                    Сервер
                  </SortableTh>
                  <Th>Тип</Th>
                  <SortableTh
                    sortKey="expires_at"
                    currentSort={sort}
                    currentDir={dir}
                    onSort={toggleSort}
                  >
                    Срок
                  </SortableTh>
                  <Th align="right" fit>
                    Действия
                  </Th>
                </tr>
              </thead>
              <tbody>
                {accesses.map((a) => (
                  <tr
                    key={a.id}
                    className={`group hover:bg-white/[0.03] transition-colors ${
                      a.is_active ? "" : "opacity-55"
                    }`}
                  >
                    <Td>
                      <div className="min-w-0">
                        <Link
                          to={`/users/${a.user_id}`}
                          className="block truncate text-zinc-100 hover:text-brand font-medium"
                        >
                          {a.user?.username ? `@${a.user.username}` : `#${a.user_id}`}
                        </Link>
                        <div className="text-[11px] text-zinc-500 font-mono">
                          {a.user?.telegram_id ?? `user #${a.user_id}`} · доступ #{a.id} ·{" "}
                          {a.secret.slice(0, 8)}…
                        </div>
                      </div>
                    </Td>
                    <Td>
                      <Link
                        to={`/servers/${a.server_id}`}
                        className="text-zinc-300 hover:text-brand"
                      >
                        {a.server?.name ?? `#${a.server_id}`}
                      </Link>
                      {a.server?.host && (
                        <div className="text-[11px] text-zinc-600 font-mono">{a.server.host}</div>
                      )}
                    </Td>
                    <Td>
                      <div className="flex items-center gap-2">
                        <Pill tone={a.access_type === "paid" ? "success" : "neutral"}>
                          {a.access_type === "paid" ? "платный" : "тест"}
                        </Pill>
                        {!a.is_active && <Pill tone="neutral">выключен</Pill>}
                      </div>
                    </Td>
                    <Td>{renderTerm(a)}</Td>
                    <Td align="right" fit>
                      <div className="flex gap-2 justify-end opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                        <Button
                          variant="ghost"
                          disabled={extend.isPending}
                          onClick={() => extend.mutate({ id: a.id, days: 30 })}
                        >
                          +30 дней
                        </Button>
                        {a.is_active && (
                          <Button
                            variant="danger"
                            disabled={deactivate.isPending}
                            onClick={() => deactivate.mutate(a.id)}
                          >
                            Деактивировать
                          </Button>
                        )}
                        <Button
                          variant="danger"
                          disabled={remove.isPending}
                          onClick={async () => {
                            if (await confirmPopup({ title: "Удалить доступ?", body: `#${a.id} — это нельзя отменить.`, danger: true, confirmLabel: "Удалить" })) {
                              remove.mutate(a.id);
                            }
                          }}
                          title="Удалить доступ"
                        >
                          <TrashIcon />
                        </Button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          {total > pageSize && (
            <PaginationBottom
              total={total}
              page={page}
              pageSize={pageSize}
              onPageChange={setPage}
            />
          )}
        </>
      )}
    </div>
  );
}

/** Срок одной ячейкой: сколько осталось + дата, без арифметики в уме. */
function renderTerm(a: Access) {
  const left = Math.ceil((new Date(a.expires_at).getTime() - Date.now()) / 86400000);
  const expired = left <= 0;
  return (
    <div className="min-w-0">
      <div
        className={`text-sm tabular-nums ${
          expired ? "text-zinc-500" : left <= 3 ? "text-amber-300" : "text-zinc-200"
        }`}
      >
        {expired ? "истёк" : `${left} дн.`}
      </div>
      <div className="text-[11px] text-zinc-600 tabular-nums">{fmt(a.expires_at)}</div>
    </div>
  );
}

function FilterGroup<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { key: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      {options.map((o) => (
        <button
          key={o.key || "_all"}
          onClick={() => onChange(o.key)}
          className={`h-8 px-3 rounded-lg text-sm border transition-colors ${
            value === o.key
              ? "bg-brand-soft text-brand glow-ring border-transparent"
              : "border-transparent text-zinc-400 hover:text-zinc-200 hover:bg-white/[0.04]"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
